import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledAgendaEventTypeButton} from '@/features/agenda/components/agenda-event-form/agenda-event-type-selector/style'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {EventCategory} from '@/features/agenda/types'
import {appointmentCategoryMapToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaEventTypeSchema = AgendaEventFieldsSchema.pick({
    category: true,
    status: true,
    patients: true,
    isGroupTreatment: true,
    consultants: true,
    duration: true,
    isRemote: true,
    remoteUrl: true,
    accessType: true
})
type AgendaEventTypeSchema = z.infer<typeof AgendaEventTypeSchema>

export const AgendaEventTypeSelector = ({mode}: {mode?: 'create' | 'edit'}) => {
    const {t} = useTranslation()
    const {control, setValue, formState, reset} = useFormContext<AgendaEventTypeSchema>()
    const category = useWatch({control, name: 'category'})
    const patients = useWatch({control, name: 'patients'})
    const consultants = useWatch({control, name: 'consultants'})

    const onChangeEventTypeSideEffects = (category: EventCategory) => {
        // if the user come back to the same category, reset the form
        if (!!formState.defaultValues?.category && formState.defaultValues.category == category && mode == 'edit') {
            reset()
            return
        }

        setValue('category', category, {shouldDirty: true})
        setValue('status', 'provisional', {shouldDirty: true})
        setValue('duration', null, {shouldDirty: true})
        setValue('isRemote', null, {shouldDirty: true})
        setValue('remoteUrl', null, {shouldDirty: true})
        setValue('isGroupTreatment', false, {shouldDirty: true})
        setValue('patients', patients?.[0] ? [patients[0]] : [], {shouldDirty: true})
        setValue('consultants', consultants?.[0] ? [{...consultants[0], accessType: null}] : [], {
            shouldDirty: true
        })
    }

    return (
        <Flexbox gap={1}>
            {ObjectKeys(appointmentCategoryMapToLabel).map(key => (
                <Controller
                    key={key}
                    control={control}
                    name="category"
                    render={({field}) => (
                        <StyledAgendaEventTypeButton
                            variant={'ghost'}
                            $isSelected={category == key}
                            shape="roundedRectangle"
                            key={key}
                            onClick={() => {
                                field.onChange(key)
                                onChangeEventTypeSideEffects(key)
                            }}
                        >
                            {t(appointmentCategoryMapToLabel[key])}
                        </StyledAgendaEventTypeButton>
                    )}
                />
            ))}
        </Flexbox>
    )
}
